import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { IconPicker } from '@/features/objects/components/IconPicker'
import { useCreateManufacturer, useUpdateManufacturer } from '../hooks'
import type { Manufacturer } from '@/types/entities'

const manufacturerSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    icon: z.string().nullable().optional(),
})

type ManufacturerFormData = z.infer<typeof manufacturerSchema>

interface ManufacturerDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    manufacturer: Manufacturer | null
}

export function ManufacturerDialog({ open, onOpenChange, manufacturer }: ManufacturerDialogProps) {
    const { t } = useTranslation()
    const isEditing = !!manufacturer
    const createManufacturer = useCreateManufacturer()
    const updateManufacturer = useUpdateManufacturer()

    const form = useForm<ManufacturerFormData>({
        resolver: zodResolver(manufacturerSchema),
        mode: 'onChange',
        defaultValues: {
            name: '',
            icon: null,
        },
    })

    useEffect(() => {
        if (open) {
            if (manufacturer) {
                form.reset({
                    name: manufacturer.name || '',
                    icon: manufacturer.icon != null ? String(manufacturer.icon) : null,
                })
            } else {
                form.reset({
                    name: '',
                    icon: null,
                })
            }
        }
    }, [open, manufacturer, form])

    const onSubmit = async (data: ManufacturerFormData) => {
        try {
            const submitData = {
                name: data.name,
                icon: data.icon != null && data.icon !== '' ? Number(data.icon) : null,
            }
            
            if (isEditing && manufacturer) {
                await updateManufacturer.mutateAsync({
                    id: manufacturer.id,
                    data: submitData,
                })
            } else {
                await createManufacturer.mutateAsync(submitData)
            }
            onOpenChange(false)
        } catch (error) {
            // Error handling is done in the hooks
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>
                        {isEditing ? 'Edit Manufacturer' : 'Create Manufacturer'}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditing
                            ? 'Update the manufacturer details.'
                            : 'Create a new manufacturer.'}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="name">Name <span className="text-destructive">*</span></Label>
                        <Input
                            id="name"
                            {...form.register('name')}
                            placeholder="Manufacturer name"
                            autoFocus
                        />
                        {form.formState.errors.name && (
                            <p className="text-sm text-destructive">
                                {form.formState.errors.name.message}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label>{t('manufacturers.icon', 'Icon')}</Label>
                        <Controller
                            control={form.control}
                            name="icon"
                            render={({ field }) => (
                                <IconPicker
                                    value={field.value ?? null}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={createManufacturer.isPending || updateManufacturer.isPending}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            type="submit"
                            disabled={createManufacturer.isPending || updateManufacturer.isPending}
                        >
                            {createManufacturer.isPending || updateManufacturer.isPending
                                ? t('common.saving')
                                : isEditing
                                ? t('common.save')
                                : 'Create'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
